import { DEX_TYPE } from "@/config/constants";
import { ATTACHMENT_FIELD_KEY } from "@/pages/shared-data/share-data-functions";
import addFormats from "@/utils/custom-ajv-formats";
import { feature, features } from '@/utils/feature-verifier';
import Ajv from "ajv";
import { unflatten } from "flat";
import { isObject } from "lodash";
import Papa from "papaparse";
import mergeDeep from "./merge-deep";

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const shouldBeBoolean = (str) => {
	return str.toLowerCase() === "true" || str.toLowerCase() === "false";
};

function checkForArrayProperties(schema) {
	let arrayProperties = [];

	const extractArrays = (schemaObj, parentKey = null) => {
		if (schemaObj?.properties) {
			Object.entries(schemaObj.properties).forEach(([key, value]) => {
				// Check if the property itself is an array
				if ((value.type && value.type.includes("array")) ||
					(value["x-uitype"] && value["x-uitype"] === "file-upload")) {
					arrayProperties.push(key);
				}

				// Handle nested oneOf/anyOf within properties
				if (value.oneOf || value.anyOf) {
					const nestedSchemas = value.oneOf || value.anyOf;
					nestedSchemas.forEach(nestedSchema => {
						extractArrays(nestedSchema, key);
					});
				}
			});
		}

		// Handle the case where the schema object itself is an array
		if (schemaObj?.type === "array" && schemaObj.items?.type === 'object') {
			if (parentKey) {
				arrayProperties.push(parentKey);
			}
			if (schemaObj.items.properties) {
				extractArrays(schemaObj.items);
			}
		}

		// Handle oneOf/anyOf at the same level
		if (schemaObj.oneOf || schemaObj.anyOf) {
			const nestedSchemas = schemaObj.oneOf || schemaObj.anyOf;
			nestedSchemas.forEach(nestedSchema => {
				extractArrays(nestedSchema);
			});
		}
	};

	extractArrays(schema);

	return arrayProperties;
}

export const formatJSON = async (payload, schema, dex) => {
	prepareJSON(payload, schema);
	if (schema?.anyOf || schema?.oneOf) {
		const tempSchema = findTempSchema(payload, schema);
		if (tempSchema) {
			schema = tempSchema;
		}
	}
	const { properties } = schema;
	const arrays = checkForArrayProperties(schema)

	const fileUploadFields = Object.entries(properties)
		.filter((entry) => entry[1]["x-uitype"] && entry[1]["x-uitype"] === "file-upload")
		.map((entry) => entry[0]);

	const transformedPayload = payload.reduce((arr, current) => {
		let newObj = {};

		for (const property in current) {
			const isArray = arrays.includes(property);
			const isCurrentPropertyObject = isObject(current[property]);
			newObj[property] = isArray && isCurrentPropertyObject ? [current[property]] : current[property];
		}

		return [...arr, newObj];
	}, []);

	let key = 1;

	const hashedJson = transformedPayload.reduce((obj, current, index) => {
		if (index === 0) {
			obj[key] = current;
			return obj;
		}

		const isSub = Object.keys(current)
			.filter((key) => !arrays.includes(key))
			.every((key) => current[key] === "");

		if (isSub) {
			Object.keys(current)
				.filter((key) => arrays.includes(key))
				.forEach((key) => {
					if (
						Object.values(current[key][0]).every(
							(value) => value === ""
						)
					) {
						current[key] = [];
					}
				});
			const newObj = mergeDeep(current, obj[key]);
			obj[key] = newObj;
			return obj;
		}

		key = key + 1;
		obj[key] = { ...current };
		return obj;
	}, {});

	const finalJson = Object.values(hashedJson);

	if ((dex === DEX_TYPE.SGHEALTHDEX || dex === DEX_TYPE.SGBUILDEX)) {
		// Format file upload fields
		finalJson.forEach((entry) => {
			for (const property in entry) {
				if (fileUploadFields.includes(property)) {
					entry[property] = {
						[ATTACHMENT_FIELD_KEY]: entry[property]?.map((prop) => prop[ATTACHMENT_FIELD_KEY])
					}
				}
			}
		});
	}

	return finalJson;
};

const removeAsteriskFromCSVHeaders = (buffer) => {
	const data = buffer.split("\n");
	data[0] = data[0].replace(/\*/g, "");
	return data.join("\n");
};
export const convertCSVtoJSON = async (buffer, schema, dex) => {
	buffer = removeAsteriskFromCSVHeaders(buffer);
	return new Promise(async (resolve) => {
		const { data } = Papa.parse(buffer, {
			header: true,
			dynamicTyping: false,
		});

		const newdata = data.filter((d) => Object.values(d).some((e) => e));
		let convertedJSON = newdata.map((entry) => {
			let newObj = {};

			for (const property in entry) {
				const hasNested = property.split(".");

				if (hasNested.length > 2) {
					if (shouldBeBoolean(entry[property])) {
						if (!newObj[hasNested[0]]) {
							newObj[hasNested[0]] = {
								[hasNested[1]]: {
									[hasNested[2]]: entry[property].toLowerCase() === "true",
									...newObj[hasNested[0]],
								}
							};
						}
						else {
							newObj[hasNested[0]] = {
								[hasNested[1]]: {
									[hasNested[2]]: entry[property].toLowerCase() === "true",
									...newObj[hasNested[0]][hasNested[1]],
								}
							};
						}

					} else {
						if (!newObj[hasNested[0]]) {
							newObj[hasNested[0]] = {
								[hasNested[1]]: {
									[hasNested[2]]: entry[property] ?? "",
									...newObj[hasNested[0]],
								}
							};
						}
						else {
							newObj[hasNested[0]] = {
								[hasNested[1]]: {
									[hasNested[2]]: entry[property] ?? "",
									...newObj[hasNested[0]][hasNested[1]],
								}
							};
						}

					}
				}
				else if (hasNested.length > 1) {
					if (shouldBeBoolean(entry[property])) {
						newObj[hasNested[0]] = {
							[hasNested[1]]:
								entry[property].toLowerCase() === "true",
							...newObj[hasNested[0]],
						};
					} else {
						newObj[hasNested[0]] = {
							[hasNested[1]]: entry[property] ?? "",
							...newObj[hasNested[0]],
						};
					}
				}
				else {
					if (shouldBeBoolean(entry[property])) {
						newObj[property] =
							entry[property].toLowerCase() === "true";
					} else {
						newObj[property] = entry[property] ?? "";
					}
				}
			}

			return newObj;
		});

		convertedJSON = await formatJSON(convertedJSON, schema, dex);

		resolve({
			convertedJSON,
		});
	});
};

export const transformCSVtoJSONV2 = async (buffer, schema) => {
    buffer = removeAsteriskFromCSVHeaders(buffer);
    return new Promise(async (resolve) => {
        // First parse to get headers
        const { data: [headers, ...rows] } = Papa.parse(buffer, {
            skipEmptyLines: true
        });

        // Create a map of column name to column index
        const columnMap = {};
        headers.forEach((header, index) => {
            columnMap[header.replace(/\*/g, '')] = index + 1; // 1-based column index
        });

        // Now parse with headers
        const { data } = Papa.parse(buffer, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true
        });

        // Add row numbers and filter empty rows
        const newData = data
            .map((dataRow, idx) => {
                // Create a map of field to its cell position
                const cellMap = {};
                Object.keys(dataRow).forEach(key => {
                    if (columnMap[key]) {
                        cellMap[key] = {
                            row: idx + 2, // +2 for header row and 1-based indexing
                            col: columnMap[key]
                        };
                    }
                });
                return { 
                    ...dataRow,
                    __rowNumber: idx + 2,
                    __cellMap: cellMap
                };
            })
            .filter(dataRow => Object.keys(dataRow).some(key => !['__rowNumber', '__cellMap'].includes(key) && dataRow[key]));

        // loop through the data to unflatten each payload and format the JSON payload
        // Store metadata separately from the actual payload to prevent AJV validation errors
        const rowMetadata = [];
        
        let convertedJSON = newData.map((dataRow, index) => {
            const { __rowNumber, __cellMap, ...rowData } = dataRow;
            
            // Store metadata separately
            rowMetadata[index] = { __rowNumber, __cellMap };
            
            // Process the actual payload data without metadata
            const unflattenJSON = unflatten(rowData);
            const updatedJSON = formatJSONData(unflattenJSON, schema);
            // Return only the payload data without metadata
            return updatedJSON;
        });

        resolve({
            convertedJSON,
            columnMap,
            rowMetadata // Include the metadata in the response
        });
    });
};

export const transformCSVtoJSON = async (buffer, schema) => {
    buffer = removeAsteriskFromCSVHeaders(buffer);
    return new Promise(async (resolve) => {
        const { data } = Papa.parse(buffer, {
            header: true,
            dynamicTyping: false,
        });

		//remove empty row
		const newData = data.filter((dataRow) => Object.values(dataRow).some((dataValue) => dataValue));
		// console.log("newData", newData)

		// loop through the data to unflatten each payload and format the JSON payload
		let convertedJSON = newData.map((dataRow) => {
			const unflattenJSON = unflatten(dataRow);
			const updatedJSON =  formatJSONData(unflattenJSON, schema);
			return updatedJSON;
		})

		// console.log("test unflatted", convertedJSON)
        resolve({
            convertedJSON
        });
    });
};


export const formatJSONData = (jsonData, schema) => {

	if (schema?.anyOf || schema?.oneOf) {
		const tempSchema = findTempSchema(jsonData, schema);
		if (tempSchema) {
			schema = tempSchema;
		}
	}
	// Get the properties from the schema
	const { properties } = schema;

	// Iterate over all properties in the schema properties
	for (let key in properties ){
		let schemaProp = properties[key];
		let jsonDataValue = jsonData[key];

		if(jsonDataValue){
			// Handle nested objects
			if (
				(schemaProp.type === "object" || (Array.isArray(schemaProp?.type) && schemaProp?.type?.includes("object"))) &&
				jsonDataValue !== null && typeof jsonDataValue === 'object'
			) {
				const notAttachmentField = !schemaProp["x-uitype"] && schemaProp["x-uitype"] !== "file-upload" ? true : false;
				if (notAttachmentField) {
					formatJSONData(jsonDataValue, schemaProp);
				}

			}

			// Handle arrays
			else if (
				(schemaProp.type === "array" || (Array.isArray(schemaProp?.type) && schemaProp?.type?.includes("array"))) &&
				Array.isArray(jsonDataValue)
			) {
				jsonDataValue.forEach((item, index)=>{
					if (schemaProp.items.type === "number" || schemaProp.items.type === "integer") {
                        jsonDataValue[index] = +(item || 0);
                    } else if (schemaProp.items.type === "object") {
                        formatJSONData(item, schemaProp.items);
                    }
				})
			}

			 // Handle booleans
			 else if (
				schemaProp.type === "boolean" ||
				(Array.isArray(schemaProp?.type) && schemaProp?.type?.includes("boolean"))
			 ) {
				jsonData[key] = jsonDataValue.toLowerCase() === "true" ;
			}

			// Handle numbers
			else if (
				schemaProp.type === "number" ||
				schemaProp.type === "integer" ||
				(Array.isArray(schemaProp?.type) && (schemaProp?.type?.includes("number") || schemaProp?.type?.includes("integer")))
			) {
				jsonData[key] = +(jsonDataValue || 0);
			}
		}
		// Remove empty values
		if(jsonDataValue === "" || jsonDataValue === null){
			delete jsonData[key];
		}
	}
	return jsonData;
}

export const findMatchingSchema = (payload, schema) => {
	try {
		const validate = ajv.compile(schema);
		for (const pload of payload) {
			if (!validate(pload)) return false;
		}
		return true;
	} catch (error) {
		throw new Error(error);
	}
};

export const findTempSchema = (payload, schema) => {
	const schemas = schema?.oneOf || schema?.anyOf;
	for (const s of schemas) {
		if (findMatchingSchema(payload, s)) {
			return s;
		}
	}
	return null;
};

export const prepareJSON = (payload, schema) => {
	try {
		if (schema?.anyOf || schema?.oneOf) {
			const tempSchema = findTempSchema(payload, schema);
			if (tempSchema) {
				schema = tempSchema;
			} else {
				throw new Error("Payload does not matches with any schema");
			}
		}
		const { properties } = schema;

		Object.entries(properties).forEach((entry) => {
			const [key, value] = entry;

			payload.forEach((pload) => {
				if (pload[key] === "" || pload[key] === null) delete pload[key];
			});

			if (
				value?.type === "number" ||
				value?.type === "integer" ||
				(Array.isArray(value?.type) &&
					(value?.type?.includes("number") ||
						value?.type?.includes("integer")))
			) {
				payload.forEach((pload) => {
					if (pload[key]) {
						pload[key] = +(pload[key] || 0);
					}
				});
			} else if (value?.type?.includes("array")) {
				payload.forEach((pload) => {
					if (pload[key] !== undefined) {
						prepareJSON(
							Array.isArray(pload[key])
								? pload[key]
								: [pload[key]],
							value.items
						);
					}
				});
			} else if (value?.type?.includes("object")) {
				const notAttachmentField = !value["x-uitype"] && value["x-uitype"] !== "file-upload" ? true : false;
				if (notAttachmentField) {
					payload.forEach((pload) => {
						prepareJSON(
							Array.isArray(pload[key]) ? pload[key] : [pload[key]],
							value.items ?? value
						);
					});
				}
			}
		});
	} catch (err) {
		throw new Error(err);
	}
};

export const selectSchema = (payload, schema) => {
	if (schema?.oneOf || schema?.anyOf) {
		return (schema.oneOf || schema.anyOf).find(
			(one) =>
				one.required.every((required) =>
					Object.keys(payload[0]).includes(required)
				) &&
				(one.additionalProperties ||
					Object.keys(payload[0]).every((key) =>
						Object.keys(one.properties).includes(key)
					))
		);
	} else {
		return schema;
	}
};

export const exportsForTest = {
	formatJSON,
	removeAsteriskFromCSVHeaders,
	shouldBeBoolean,
	checkForArrayProperties,
	transformCSVtoJSON
};
